import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TablerIconsModule } from 'angular-tabler-icons';
import RecordRTC from 'recordrtc';
import { MaterialModule } from 'src/app/material.module';
import { TranscriptionService } from 'src/app/services/apps/audio/transcription.service';
import { EntityExtractorService } from 'src/app/services/apps/entity-extractor/entity-extractor.service';
import { DemandModalData } from 'src/assets/types/interface/demand-modal-data.interface';
import { AddDemandModalComponent } from '../add/add-demand.component';
import { catchError, finalize, of } from 'rxjs';
import { Company } from 'src/assets/types/interface/company.interface';
import {
  DemandServiceType,
  DemandSpecialty,
} from 'src/assets/types/interface/demand.interface';
import { Environment } from 'src/assets/types/interface/environment.interface';
import { Equipment } from 'src/assets/types/interface/equipament.interface';

interface NormalizedAiDataResult {
  data: DemandModalData;
  partial: boolean;
}

@Component({
  selector: 'app-ai-demand',
  standalone: true,
  imports: [
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    TablerIconsModule,
  ],
  templateUrl: 'ai-demand.component.html',
  styleUrl: 'ai-demand.component.scss',
})
export class AddDemandAiModalComponent implements OnInit, OnDestroy {
  readonly minimumDescriptionLength = 10;

  showTextField = true;
  isRecording = false;
  audioUrl: string | null = null;
  audioCapturePermission = false;

  private stream: MediaStream | null = null;
  private recorder: RecordRTC | null = null;

  mediaRecorder: MediaRecorder | null = null;
  chunks: Blob[] = [];
  recordedBlob: Blob | null = null;

  data: DemandModalData;

  occurrence: string = '';

  isExtracting = false;
  isTranscribing = false;
  errorMessage = '';
  aiWarningMessage = '';

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<AddDemandAiModalComponent>,
    private cdr: ChangeDetectorRef,
    private transcriptionService: TranscriptionService,
    private entityExtractorService: EntityExtractorService,
  ) {}

  ngOnInit(): void {
    this.getAudioCapturePermission();
  }

  closeDialog(): void {
    this.dialogRef.close({ event: 'Cancel' });
  }

  ngOnDestroy(): void {
    this.transcriptionService.cancelTranscription();
    this.stopActiveMediaRecorder();
    this.revokeAudioUrl();
    this.cleanup(false);
  }

  openDialogCreateDemand(withIA: boolean = false) {
    this.transcriptionService.cancelTranscription();
    this.errorMessage = '';
    this.aiWarningMessage = '';

    if (withIA) {
      if (!this.canUseAi) {
        this.errorMessage =
          'Descreva o problema com pelo menos 10 caracteres para usar a IA.';
        return;
      }

      if (this.isLoading) return;

      this.isExtracting = true;
      try {
        this.entityExtractorService
          .recommendCompanyEnvironment({
            text: this.occurrence.trim(),
          })
          .pipe(
            finalize(() => {
              this.isExtracting = false;
            }),
            catchError((error) => {
              this.errorMessage =
                error?.error?.message ||
                'Nao foi possivel preencher a solicitacao via IA agora.';
              return of(null);
            }),
          )
          .subscribe((res) => {
            if (!res) return;

            const normalized = this.normalizeAiData(res);
            this.data = normalized.data;

            if (normalized.partial) {
              this.aiWarningMessage =
                'A IA retornou dados parciais. Revise os campos sugeridos antes de continuar.';
            }

            this.openDialog(true);
          });
      } catch (error: any) {
        this.isExtracting = false;

        this.errorMessage =
          error?.error?.message ||
          'Nao foi possivel preencher a solicitacao via IA agora.';
        return;
      }
    } else {
      this.openDialog(false);
    }
  }

  private openDialog(withAi: boolean) {
    this.closeDialog();

    const dialogRef = this.dialog.open(AddDemandModalComponent, {
      width: 'min(1180px, calc(100vw - 48px))',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100vh - 24px)',
      autoFocus: false,
      data: {
        data: this.data ?? null,
        occurrence: this.occurrence,
        withAi,
        aiWarningMessage: this.aiWarningMessage,
      },
    });

    dialogRef.afterClosed().subscribe();
    this.cdr.detectChanges();
  }

  //
  async startRecording(): Promise<void> {
    try {
      this.errorMessage = '';
      this.transcriptionService.cancelTranscription();
      if (this.stream) this.forceResetRecording();

      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      this.chunks = [];

      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType: 'audio/webm',
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) this.chunks.push(event.data);
      };

      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: 'audio/webm' });
        this.audioUrl = URL.createObjectURL(blob);
        this.recordedBlob = blob;
        this.cleanup();
      };

      this.mediaRecorder.start();
      this.isRecording = true;
      this.audioUrl = null;
      this.cdr.detectChanges();
    } catch (err) {
      console.error('Erro ao acessar microfone:', err);
      this.errorMessage =
        'Nao foi possivel acessar o microfone. Verifique a permissao do navegador.';
      this.forceResetRecording();
    }
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
    }
  }

  cancelRecording(): void {
    if (this.recorder && this.isRecording) {
      this.recorder.stopRecording(() => this.cleanup());
    } else {
      this.cleanup();
    }
  }

  //
  deleteRecording(): void {
    this.revokeAudioUrl();
    this.recordedBlob = null;
    this.showTextField = true;
    this.cdr.detectChanges();
  }

  transcribe(): void {
    this.errorMessage = '';

    if (this.isLoading) return;

    if (!this.recordedBlob) {
      this.errorMessage = 'Grave um audio antes de prosseguir.';
      return;
    }

    this.isTranscribing = true;
    const formData = new FormData();
    const audioFile = new File([this.recordedBlob], 'gravacao.webm', {
      type: 'audio/webm',
    });

    formData.append('file', audioFile);
    this.transcriptionService
      .getTranscription(formData)
      .pipe(
        finalize(() => {
          this.isTranscribing = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response: any) => {
          const transcription = this.extractTranscriptionText(response);
          this.occurrence = transcription || '';

          if (!transcription) {
            this.errorMessage =
              'Nao foi possivel identificar texto no audio gravado.';
          }

          this.showTextField = true;
          this.cleanup();
        },
        error: (error) => {
          this.errorMessage =
            error?.error?.message ||
            'Nao foi possivel transcrever o audio neste momento.';
          this.cleanup();
        },
      });

    this.cdr.detectChanges();
  }

  //
  toggleTextField(): void {
    this.forceResetRecording();
    this.showTextField = !this.showTextField;

    this.isRecording ? this.stopRecording() : this.startRecording();
  }

  switchToTextMode(): void {
    this.forceResetRecording();
  }

  getInstructionMessage(): string | null {
    if (this.showTextField) {
      return 'Forneça detalhes para a solicitação de serviço, como: Ambiente, Ativos e Informações do problema.';
    }

    if (!this.audioCapturePermission) {
      return 'Habilite a permissão do navegador para capturar áudio.';
    }

    if (!this.audioUrl && !this.isRecording) {
      return 'Clique para iniciar a gravação de áudio.';
    }

    if (!this.audioUrl && this.isRecording) {
      return 'Gravando áudio.';
    }

    if (this.audioUrl) {
      return 'Confira se o áudio está corretamente gravado e clique para prosseguir.';
    }

    return null;
  }

  //
  private forceResetRecording(): void {
    this.cleanup();
    this.revokeAudioUrl();
    this.showTextField = true;
    this.cdr.detectChanges();
  }

  //
  private cleanup(shouldDetectChanges = true): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
    }

    this.recorder = null;
    this.mediaRecorder = null;
    this.stream = null;
    this.chunks = [];
    this.isRecording = false;

    if (shouldDetectChanges) {
      this.cdr.detectChanges();
    }
  }

  private stopActiveMediaRecorder(): void {
    if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
      return;
    }

    this.mediaRecorder.ondataavailable = null;
    this.mediaRecorder.onstop = null;
    this.mediaRecorder.stop();
  }

  private revokeAudioUrl(): void {
    if (this.audioUrl) {
      URL.revokeObjectURL(this.audioUrl);
    }

    this.audioUrl = null;
  }

  private getAudioCapturePermission(): void {
    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((status) => {
        this.audioCapturePermission = status.state === 'granted';

        status.onchange = () => {
          this.audioCapturePermission = status.state === 'granted';
        };
      })
      .catch((error) => {
        console.error('Erro ao verificar permissão de microfone:', error);
        this.audioCapturePermission = false;
      });
  }

  validTextToIa() {
    return this.canUseAi;
  }

  get canUseAi(): boolean {
    return this.occurrence.trim().length >= this.minimumDescriptionLength;
  }

  get isLoading(): boolean {
    return this.isExtracting || this.isTranscribing;
  }

  get occurrenceLength(): number {
    return this.occurrence.trim().length;
  }

  get primaryInstruction(): string {
    if (!this.showTextField && this.audioUrl) {
      return 'Confira a gravacao antes de transcrever.';
    }

    if (!this.showTextField && this.isRecording) {
      return 'Gravando audio.';
    }

    if (!this.showTextField) {
      return this.audioCapturePermission
        ? 'Clique no microfone para iniciar a gravacao.'
        : 'Habilite a permissao de microfone no navegador.';
    }

    return 'Descreva o problema para abrir a solicitacao.';
  }

  get helperInstruction(): string {
    if (!this.showTextField) {
      return 'Depois da transcricao, voce podera revisar o texto antes de continuar.';
    }

    return 'Inclua unidade, ambiente, ativo e detalhes do problema quando souber.';
  }

  private normalizeAiData(response: unknown): NormalizedAiDataResult {
    if (!this.isRecord(response)) {
      return {
        data: this.createEmptyDemandModalData(),
        partial: true,
      };
    }

    const companies = this.normalizeList<Company>(
      response['companies'],
      this.isValidCompany,
    );
    const environments = this.normalizeList<Environment>(
      response['environments'],
      this.isValidEnvironment,
    );
    const specialties = this.normalizeList<DemandSpecialty>(
      response['specialties'],
      this.isValidDemandSpecialty,
    );
    const selectedSpecialties = this.normalizeList<DemandSpecialty>(
      response['selectedSpecialties'],
      this.isValidDemandSpecialty,
    );
    const equipments = this.normalizeList<Equipment>(
      response['equipments'],
      this.isValidEquipment,
    );
    const types = this.normalizeList<DemandServiceType>(
      response['types'],
      this.isValidDemandServiceType,
    );

    return {
      data: {
        companies: companies.items,
        environments: environments.items,
        specialties: specialties.items,
        selectedSpecialties: selectedSpecialties.items,
        equipments: equipments.items,
        types: types.items,
        occurrence: this.hasText(response['occurrence'])
          ? response['occurrence']
          : this.occurrence,
        needsTechnicalVisit:
          typeof response['needsTechnicalVisit'] === 'boolean'
            ? response['needsTechnicalVisit']
            : false,
      },
      partial:
        companies.partial ||
        environments.partial ||
        specialties.partial ||
        selectedSpecialties.partial ||
        equipments.partial ||
        types.partial,
    };
  }

  private createEmptyDemandModalData(): DemandModalData {
    return {
      companies: [],
      environments: [],
      specialties: [],
      selectedSpecialties: [],
      equipments: [],
      occurrence: this.occurrence,
      types: [],
      needsTechnicalVisit: false,
    };
  }

  private normalizeList<T>(
    value: unknown,
    isValidItem: (item: unknown) => item is T,
  ): { items: T[]; partial: boolean } {
    if (!Array.isArray(value)) {
      return {
        items: [],
        partial: true,
      };
    }

    const items = value.filter(isValidItem);

    return {
      items,
      partial: items.length !== value.length,
    };
  }

  private extractTranscriptionText(response: unknown): string {
    const body = this.isRecord(response) ? response['body'] : null;
    const responseBody = this.isRecord(body) ? body : response;

    if (!this.isRecord(responseBody)) {
      return '';
    }

    const transcription = responseBody['transcription'];

    if (this.isRecord(transcription) && this.hasText(transcription['text'])) {
      return transcription['text'].trim();
    }

    if (this.hasText(transcription)) {
      return transcription.trim();
    }

    if (this.hasText(responseBody['text'])) {
      return responseBody['text'].trim();
    }

    return '';
  }

  private isValidCompany = (item: unknown): item is Company => {
    if (!this.isRecord(item)) return false;

    return this.hasText(item['uuid']);
  };

  private isValidEnvironment = (item: unknown): item is Environment => {
    if (!this.isRecord(item)) return false;

    return this.hasText(item['uuid']) && this.hasText(item['name']);
  };

  private isValidEquipment = (item: unknown): item is Equipment => {
    if (!this.isRecord(item)) return false;

    return this.hasText(item['uuid']) && this.hasText(item['name']);
  };

  private isValidDemandSpecialty = (
    item: unknown,
  ): item is DemandSpecialty => {
    if (!this.isRecord(item)) return false;

    return this.hasText(item['uuid']) && this.hasText(item['name']);
  };

  private isValidDemandServiceType = (
    item: unknown,
  ): item is DemandServiceType => {
    if (!this.isRecord(item)) return false;

    return this.hasText(item['uuid']) && this.hasText(item['name']);
  };

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private hasText(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }
}
